import React, { useState, useEffect } from 'react';
import { adminAPI, DocumentVerification as DocumentVerificationType, DocumentSummary } from '../../services/api';
import { formatMimeLabel } from '../../utils/documentDisplay';
import './DocumentVerification.css';

const DocumentVerification: React.FC = () => {
  const [documents, setDocuments] = useState<DocumentVerificationType[]>([]);
  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<DocumentVerificationType | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verificationAction, setVerificationAction] = useState<'verify' | 'reject'>('verify');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);

  // Fetch documents from API
  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getDocumentVerification({
          status: filterStatus === 'all' ? undefined : filterStatus,
          page: currentPage,
          limit: 10
        });
        
        if (response.success) {
          setDocuments(response.data.documents);
          setSummary(response.data.summary);
          setTotalPages(response.data.pagination.pages);
        } else {
          setError(response.message || 'Failed to fetch documents');
        }
      } catch (err) {
        setError('Failed to fetch documents');
        console.error('Error fetching documents:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [filterStatus, currentPage]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified': return '#2ECC40';
      case 'rejected': return '#FF4136';
      case 'pending_review': return '#EA580C';
      default: return '#64748B';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleVerificationAction = async (action: 'verify' | 'reject') => {
    if (!selectedDocument) return;

    try {
      setIsProcessing(true);
      const response = await adminAPI.processDocumentVerification(selectedDocument.id, {
        action,
        notes: verificationNotes
      });

      if (response.success) {
        // Update the document in the list
        setDocuments(prev => prev.map(doc => 
          doc.id === selectedDocument.id 
            ? { 
                ...doc, 
                status: response.data.status as 'pending_review' | 'verified' | 'rejected',
                verificationNotes: response.data.notes,
                reviewedAt: response.data.verifiedAt,
                reviewerId: response.data.verifiedBy
              }
            : doc
        ));
        setShowVerificationModal(false);
        setVerificationNotes('');
        setSelectedDocument(null);
      } else {
        setError(response.message || 'Failed to process document');
      }
    } catch (err) {
      setError('Failed to process document');
      console.error('Error processing document:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const openVerificationModal = (document: DocumentVerificationType, action: 'verify' | 'reject') => {
    setSelectedDocument(document);
    setVerificationAction(action);
    setVerificationNotes('');
    setShowVerificationModal(true);
  };

  const isImageFile = (doc: DocumentVerificationType): boolean => {
    if (doc.mimeType?.includes('pdf')) return false;
    if (/\.pdf($|\?)/i.test(doc.documentUrl) || doc.fileName?.toLowerCase().endsWith('.pdf')) return false;
    if (doc.mimeType?.startsWith('image/')) return true;
    if (doc.documentUrl.includes('/image/upload/')) return true;
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(doc.documentUrl)) return true;
    return false;
  };

  useEffect(() => {
    if (!selectedDocument) {
      setPdfPreviewUrl(null);
      setPdfPreviewError('');
      setPdfLoading(false);
      return;
    }

    const created: { url: string } = { url: '' };
    let cancelled = false;
    setPdfLoading(true);
    setPdfPreviewError('');
    setPdfPreviewUrl(null);

    adminAPI.fetchDocumentFile(selectedDocument.id)
      .then((blob) => {
        if (cancelled) return;
        const type = blob.type || (isImageFile(selectedDocument) ? 'image/jpeg' : 'application/pdf');
        created.url = URL.createObjectURL(new Blob([blob], { type }));
        setPdfPreviewUrl(created.url);
      })
      .catch((err: any) => {
        if (!cancelled) setPdfPreviewError(err?.message || 'Could not load this document');
      })
      .finally(() => {
        if (!cancelled) setPdfLoading(false);
      });

    return () => {
      cancelled = true;
      if (created.url) URL.revokeObjectURL(created.url);
    };
  }, [selectedDocument]);

  const formatDetailDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const day = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const time = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${day} at ${time}`;
  };

  const openDocument = (download: boolean) => {
    if (!selectedDocument || !pdfPreviewUrl) return;
    if (download) {
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.download = selectedDocument.fileName || 'document';
      link.click();
      return;
    }
    window.open(pdfPreviewUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="document-verification">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading document verification data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="document-verification">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="document-verification">
      {/* Header */}
      <div className="verification-header">
        <h2 className="verification-title">Document Verification</h2>
        <p className="verification-subtitle">
          Review and verify NSFAS documents submitted by students
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="verification-summary">
          <div className="summary-card">
            <div className="summary-icon total">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Total</h3>
              <p className="summary-number">{summary.totalDocuments}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon pending">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Pending</h3>
              <p className="summary-number">{summary.pendingReview}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon verified">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Verified</h3>
              <p className="summary-number">{summary.verified}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon rejected">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Rejected</h3>
              <p className="summary-number">{summary.rejected}</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon time">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Avg Time</h3>
              <p className="summary-number">{summary.averageReviewTime}h</p>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-icon rate">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div className="summary-content">
              <h3>Rate</h3>
              <p className="summary-number">{(summary.verificationRate * 100).toFixed(0)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="verification-controls">
        <div className="filter-section">
          <div className="filter-group">
            <label htmlFor="status-filter" className="filter-label">Status:</label>
            <select
              id="status-filter"
              className="filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Documents</option>
              <option value="pending_review">Pending Review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        <div className="results-info">
          <span className="results-count">
            {documents.length} documents found
          </span>
        </div>
      </div>

      {/* Documents Table */}
      <div className="documents-table-container">
        <table className="documents-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Document Info</th>
              <th>Institution</th>
              <th>Status</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr
                key={document.id}
                className="document-row"
                onClick={() => setSelectedDocument(document)}
              >
                <td className="student-cell">
                  <div className="student-info">
                    <div className="student-avatar">
                      {document.borrowerName.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="student-details">
                      <span className="student-name">{document.borrowerName}</span>
                      <span className="application-id">{document.applicationId}</span>
                    </div>
                  </div>
                </td>

                <td className="document-cell">
                  <div className="document-info">
                    <span className="document-type">
                      {document.documentType.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    <span className="document-name" title={document.fileName}>
                      {document.fileName}
                    </span>
                    <span className="document-size">{formatFileSize(document.fileSize)}</span>
                  </div>
                </td>

                <td className="institution-cell">
                  <div className="institution-info">
                    <span className="institution-name">{document.metadata.institution}</span>
                    <span className="student-number">{document.metadata.studentNumber}</span>
                  </div>
                </td>

                <td className="status-cell">
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(document.status) }}
                  >
                    {document.status.replace('_', ' ').toUpperCase()}
                  </span>
                  {document.reviewedAt && (
                    <span className="reviewed-date">
                      Reviewed: {formatDate(document.reviewedAt)}
                    </span>
                  )}
                </td>

                <td className="uploaded-cell">
                  <span className="uploaded-date">
                    {formatDate(document.uploadedAt)}
                  </span>
                </td>

                <td className="actions-cell">
                  <div className="document-actions">
                    {document.status === 'pending_review' && (
                      <>
                        <button
                          className="action-btn verify-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openVerificationModal(document, 'verify');
                          }}
                          type="button"
                        >
                          ✓ Verify
                        </button>
                        <button
                          className="action-btn reject-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openVerificationModal(document, 'reject');
                          }}
                          type="button"
                        >
                          ✗ Reject
                        </button>
                      </>
                    )}
                    <button
                      className="action-btn view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedDocument(document);
                      }}
                      type="button"
                    >
                      View Details
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {documents.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
            </div>
            <h3 className="empty-title">No Documents Found</h3>
            <p className="empty-description">
              No documents match your current filter criteria.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Document Detail Modal */}
      {selectedDocument && !showVerificationModal && (
        <div className="document-modal-overlay" onClick={() => setSelectedDocument(null)}>
          <div className="document-modal document-modal--details" onClick={(e) => e.stopPropagation()}>
            <div className="doc-details-header">
              <div className="doc-details-heading">
                <h3>Document Details — {selectedDocument.borrowerName}</h3>
                <span className={`doc-status-pill doc-status-pill--${selectedDocument.status}`}>
                  {selectedDocument.status.replace('_', ' ')}
                </span>
              </div>
              <button
                className="doc-details-close"
                onClick={() => setSelectedDocument(null)}
                type="button"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="doc-details-layout">
              <aside className="doc-file-card">
                <div className="doc-file-icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <p className="doc-file-name" title={selectedDocument.fileName}>{selectedDocument.fileName}</p>
                <p className="doc-file-type">{selectedDocument.documentType.replace(/_/g, ' ')}</p>
                <p className="doc-file-size">{formatFileSize(selectedDocument.fileSize)}</p>
                <span className="doc-preview-chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Document Preview
                </span>
              </aside>

              <section className="doc-viewer">
                <div className="doc-viewer-toolbar">
                  <div className="doc-viewer-file">
                    <span className="doc-file-icon doc-file-icon--small" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </span>
                    <div>
                      <p className="doc-viewer-name" title={selectedDocument.fileName}>{selectedDocument.fileName}</p>
                      <p className="doc-viewer-meta">
                        {formatFileSize(selectedDocument.fileSize)} · {formatMimeLabel(selectedDocument.mimeType, selectedDocument.fileName)}
                      </p>
                    </div>
                  </div>
                  <div className="doc-viewer-actions">
                    <button
                      type="button"
                      className="doc-tool-btn"
                      onClick={() => openDocument(false)}
                      disabled={!pdfPreviewUrl}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                        <polyline points="15 3 21 3 21 9" />
                        <line x1="10" y1="14" x2="21" y2="3" />
                      </svg>
                      Open in new tab
                    </button>
                    <button
                      type="button"
                      className="doc-tool-btn"
                      onClick={() => openDocument(true)}
                      disabled={!pdfPreviewUrl}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
                <div className="doc-viewer-stage">
                  {pdfPreviewError ? (
                    <p className="doc-viewer-message">{pdfPreviewError}</p>
                  ) : !pdfPreviewUrl ? (
                    <p className="doc-viewer-message">{pdfLoading ? 'Loading document…' : 'Preparing preview…'}</p>
                  ) : isImageFile(selectedDocument) ? (
                    <img
                      src={pdfPreviewUrl}
                      alt={selectedDocument.fileName}
                      className="document-preview-image"
                    />
                  ) : (
                    <iframe
                      title={selectedDocument.fileName}
                      src={pdfPreviewUrl}
                      className="document-preview-frame"
                    />
                  )}
                </div>
              </section>
            </div>

            <div className="doc-info-grid">
              <article className="doc-info-card">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  Document
                </h4>
                <dl>
                  <div><dt>File name</dt><dd title={selectedDocument.fileName}>{selectedDocument.fileName}</dd></div>
                  <div><dt>Type</dt><dd>{selectedDocument.documentType.replace(/_/g, ' ')}</dd></div>
                  <div><dt>Size</dt><dd>{formatFileSize(selectedDocument.fileSize)}</dd></div>
                  <div><dt>Uploaded</dt><dd>{formatDetailDate(selectedDocument.uploadedAt)}</dd></div>
                </dl>
              </article>
              <article className="doc-info-card">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                  Student
                </h4>
                <dl>
                  <div><dt>Name</dt><dd>{selectedDocument.borrowerName}</dd></div>
                  <div><dt>Loan reference</dt><dd>{(selectedDocument.applicationId || '').slice(0, 8)}…</dd></div>
                  {selectedDocument.metadata?.institution ? (
                    <div><dt>Institution</dt><dd>{selectedDocument.metadata.institution}</dd></div>
                  ) : null}
                  {selectedDocument.metadata?.studentNumber ? (
                    <div><dt>Student no.</dt><dd>{selectedDocument.metadata.studentNumber}</dd></div>
                  ) : null}
                </dl>
              </article>
              <article className="doc-info-card">
                <h4>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                  Verification
                </h4>
                <dl>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <span className={`doc-status-pill doc-status-pill--${selectedDocument.status}`}>
                        {selectedDocument.status.replace('_', ' ')}
                      </span>
                    </dd>
                  </div>
                  {selectedDocument.reviewerId ? (
                    <div><dt>Reviewed by</dt><dd title={selectedDocument.reviewerId}>{selectedDocument.reviewerId.slice(0, 8)}…</dd></div>
                  ) : null}
                  {selectedDocument.reviewedAt ? (
                    <div><dt>Reviewed at</dt><dd>{formatDetailDate(selectedDocument.reviewedAt)}</dd></div>
                  ) : null}
                  {selectedDocument.verificationNotes ? (
                    <div><dt>Notes</dt><dd>{selectedDocument.verificationNotes}</dd></div>
                  ) : null}
                </dl>
              </article>
            </div>

            {selectedDocument.status === 'pending_review' && (
              <div className="modal-actions">
                <button
                  className="modal-btn verify-btn"
                  onClick={() => openVerificationModal(selectedDocument, 'verify')}
                  type="button"
                >
                  Verify Document
                </button>
                <button
                  className="modal-btn reject-btn"
                  onClick={() => openVerificationModal(selectedDocument, 'reject')}
                  type="button"
                >
                  Reject Document
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && selectedDocument && (
        <div className="verification-modal-overlay" onClick={() => setShowVerificationModal(false)}>
          <div className="verification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {verificationAction === 'verify' ? '✓ Verify Document' : '✗ Reject Document'} - {selectedDocument.borrowerName}
              </h3>
              <button
                className="modal-close"
                onClick={() => setShowVerificationModal(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div className="modal-content">
              <div className="verification-summary">
                <h4>Document Summary</h4>
                <div className="summary-details">
                  <p><strong>Student:</strong> {selectedDocument.borrowerName}</p>
                  <p><strong>Document:</strong> {selectedDocument.fileName}</p>
                  <p><strong>Institution:</strong> {selectedDocument.metadata.institution}</p>
                  <p><strong>Student Number:</strong> {selectedDocument.metadata.studentNumber}</p>
                </div>
              </div>

              <div className="verification-notes">
                <label htmlFor="verification-notes" className="notes-label">
                  Verification Notes:
                </label>
                <textarea
                  id="verification-notes"
                  className="notes-textarea"
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder={
                    verificationAction === 'verify' 
                      ? 'Add notes about why this document is verified...'
                      : 'Add notes about why this document is rejected...'
                  }
                  rows={4}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button
                className={`modal-btn ${verificationAction === 'verify' ? 'verify-btn' : 'reject-btn'}`}
                onClick={() => handleVerificationAction(verificationAction)}
                disabled={isProcessing}
                type="button"
              >
                {isProcessing ? 'Processing...' : `${verificationAction === 'verify' ? 'Verify' : 'Reject'} Document`}
              </button>
              <button
                className="modal-btn secondary-btn"
                onClick={() => setShowVerificationModal(false)}
                disabled={isProcessing}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVerification;